'use client';

import React from 'react';
import AppLayout from '@/components/AppLayout';
import HeroStats from './components/HeroStats';
import TrendingNow from './components/TrendingNow';
import LatestAchievements from './components/LatestAchievements';
import BiggestMovers from './components/BiggestMovers';
import dynamic from 'next/dynamic';
import { useLanguage } from '@/contexts/LanguageContext';

const ActivityChart = dynamic(() => import('./components/ActivityChart'), { ssr: false });

export default function HomePage() {
  const { t } = useLanguage();

  return (
    <AppLayout activeRoute="/">
      {/* Page Header */}
      <div className="mb-8 gradient-hero rounded-2xl p-6 sm:p-8" style={{ border: '1px solid rgba(34,197,94,0.1)' }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="pulse-dot" />
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--primary)' }}>
                {t('home_live_label')}
              </span>
            </div>
            <h1 className="text-hero font-extrabold mb-2" style={{ color: 'var(--foreground)' }}>
              {t('home_title')}
            </h1>
            <p className="text-sm max-w-lg" style={{ color: 'var(--muted-foreground)' }}>
              {t('home_desc')}
            </p>
          </div>
          <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0">
            <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
              {t('home_community_pulse')}
            </span>
            <span className="text-2xl font-extrabold text-tabular" style={{ color: 'var(--primary)' }}>
              218
            </span>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              {t('home_active_now')}
            </span>
          </div>
        </div>
      </div>

      <section className="mb-8">
        <HeroStats />
      </section>

      <section className="mb-8">
        <div className="card-glass p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold" style={{ color: 'var(--foreground)' }}>
                {t('home_activity_title')}
              </h2>
              <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                {t('home_activity_desc')}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs font-medium">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-0.5 rounded" style={{ background: 'var(--primary)' }} />
                <span style={{ color: 'var(--muted-foreground)' }}>{t('home_messages')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-0.5 rounded" style={{ background: '#8b5cf6' }} />
                <span style={{ color: 'var(--muted-foreground)' }}>{t('home_active_users')}</span>
              </div>
            </div>
          </div>
          <ActivityChart />
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 2xl:grid-cols-5 gap-6 mb-8">
        <div className="lg:col-span-1 xl:col-span-2 2xl:col-span-2 card-glass p-6">
          <TrendingNow />
        </div>
        <div className="lg:col-span-1 xl:col-span-3 2xl:col-span-3 card-glass p-6">
          <LatestAchievements />
        </div>
      </section>

      <section className="card-glass p-6 mb-8">
        <BiggestMovers />
      </section>

      <section
        className="rounded-2xl p-6 sm:p-8 text-center mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(22,163,74,0.04) 100%)',
          border: '1px solid rgba(34,197,94,0.15)',
        }}
      >
        <p className="text-sm font-semibold mb-1" style={{ color: 'var(--primary)' }}>
          {t('home_cta_title')}
        </p>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          {t('home_cta_desc')}
        </p>
      </section>
    </AppLayout>
  );
}